import { Alert, AlertProps } from '@zorro/design-system/v2/Alert';
import { List } from '@zorro/design-system/v2/List';
import { Title } from '@zorro/design-system/v2/Title';
import { InfoSquareRounded } from '@zorro/shared/assets';
import { Group, Icon, Stack, Text, brand } from '@zorro/web/design-system';
import { Children, ReactElement, ReactNode } from 'react';

type Props = AlertProps & {
  title?: string | ReactElement;
  items?: (string | ReactNode)[];
  actionButton?: ReactNode;
  whiteSpace?: 'pre-line' | 'normal';
  shouldReplaceIconToInfoSquareRounded?: boolean;
  shouldOrderListItems?: boolean;
};

export const InformationBoxComponent = ({
  title,
  items = [],
  whiteSpace,
  actionButton,
  shouldReplaceIconToInfoSquareRounded = false,
  shouldOrderListItems = false,
  variant = 'general-note',
  ...props
}: Props) => {
  const customIcon = shouldReplaceIconToInfoSquareRounded ? (
    <Icon
      icon={InfoSquareRounded}
      size="1.25rem"
      style={{
        flexShrink: 0,
        backgroundColor: 'var(--color-background-callout-tertiary)',
        borderRadius: 'var(--round-corners-4)',
        padding: 'var(--space-4)',
      }}
    />
  ) : undefined;

  return (
    <Alert
      variant={variant}
      {...props}
      {...(customIcon ? { icon: customIcon } : {})}
    >
      <Group gap="xs" wrap="nowrap" align="start">
        <Stack gap="xs">
          {title &&
            (typeof title === 'string' ? (
              <Title order={4} size="sm">
                {title}
              </Title>
            ) : (
              title
            ))}
          {items.length === 1 ? (
            <Text c={brand.zorroCoal900} style={{ whiteSpace }} size="sm">
              {items[0]}
            </Text>
          ) : (
            items.length > 0 && (
              <List
                size="sm"
                type={shouldOrderListItems ? 'ordered' : undefined}
                spacing={shouldOrderListItems ? 'md' : undefined}
              >
                {Children.map(items, (item) => (
                  <List.Item>{item}</List.Item>
                ))}
              </List>
            )
          )}
          {actionButton}
        </Stack>
      </Group>
    </Alert>
  );
};
